/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/shipment/list/filters.tsx — its
 * `FilterElements`: one `StatusField` per filter, inline on a wide toolbar with the label hidden,
 * full-width and labelled inside the drawer.
 *
 * Adapted for server-side filtering, the same way `orders/filters.tsx` is: the control writes the
 * route's `tierId` search param and the loader refetches. The search box (`q`) and the status
 * dropdown are the toolbar's own, in List.tsx; `PagedTable`'s toolbar lays this field out and owns
 * Clear.
 */
import { useNavigate } from "react-router-dom";
import { StatusField } from "@steelyard/theme";
import { FILTER_LABEL } from "../../components/table/toolbar";
import { customerStatusLabel } from "../../lib/services/customers";
import type { TierOption } from "../../components/customers/assign-terms-form";

/**
 * `CustomerStatus.cs`'s four members; the URL carries the wire number, the label is display only.
 * Status is the status dropdown on the toolbar (List.tsx), not a drawer field — one control per filter.
 */
export const CUSTOMER_STATUSES = [0, 1, 2, 3].map((value) => {
  const words = customerStatusLabel(value).replaceAll("_", " ");
  return { value: String(value), label: words.charAt(0).toUpperCase() + words.slice(1), key: customerStatusLabel(value) };
});

export function Filters({
  tierId,
  tiers,
  query,
}: {
  tierId?: string;
  tiers: TierOption[];
  query: (over: Record<string, string | undefined>) => string;
}) {
  const navigate = useNavigate();
  const tierOptions = tiers.map((tier) => ({ value: tier.id, label: tier.name }));

  // A filter change means a different result set, so it resets the page.
  const apply = (next: string | undefined) => navigate(query({ page: undefined, tierId: next }));

  return (
    <StatusField
      options={tierOptions}
      value={tierOptions.find((option) => option.value === tierId) ?? null}
      onChange={(option: { value: string } | null) => apply(option?.value)}
      clearable
      onClear={() => apply(undefined)}
      placeholder="Any tier"
      label="Tier"
      labelClassName={FILTER_LABEL}
      dropdownClassName="!z-10 h-auto"
      className="w-full @5xl:w-52"
    />
  );
}
